import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Grid,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Visibility as VisibilityIcon,
  Groups2 as Groups2Icon,
  HourglassEmpty as HourglassEmptyIcon,
  TaskAlt as TaskAltIcon,
  HighlightOff as HighlightOffIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { supplierTiles } from '../../utils/supplierTiles';
import Footer from '../../components/Footer/Footer';
import DataTablePagination from '../../components/Table/DataTablePagination';
import SearchField from '../../components/Table/SearchField';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useListPage } from '../../hooks/useListPage';
import { useAuth } from '../../context/AuthContext';
import ListFilters from '../../components/Tasks/ListFilters';
import StatusChip from '../../components/Tasks/StatusChip';
import StatusLabel from '../../components/Status/StatusLabel';
import { filtersFromSearchParams, filtersToSearchParams, filtersToApiParams, stageLabelFor } from '../../utils/listFilterParams';
const SupplierList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // A new term is a new result set; staying on page 7 of the old one shows an
  // empty table and looks like the search found nothing.
  const [page, setPage] = useListPage(JSON.stringify([debouncedSearch, rowsPerPage]));
  const [total, setTotal] = useState(0);
  const [sortOrder, setSortOrder] = useState('desc');
  // Filters live in the URL, so "suppliers answering legal" can be bookmarked
  // or sent to someone. A management user's department is pinned: the server
  // scopes them to it whatever they ask for.
  const { user } = useAuth();
  const pinnedDepartment = user?.role === 'management' ? (user.department || '') : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const filterKey = searchParams.toString();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleFiltersChange = (next) => {
    setSearchParams(filtersToSearchParams(next, searchParams), { replace: true });
    setPage(1);
  };
  const [stats, setStats] = useState(supplierTiles(null));

  useEffect(() => {
    fetchSuppliers();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortOrder, debouncedSearch, filterKey]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: rowsPerPage,
        sortOrder,
        groupBy: 'name',
        approvedRegistrationOnly: 'true',
      };
      if (debouncedSearch) params.search = debouncedSearch;
      Object.assign(params, filtersToApiParams(filters));

      const response = await api.get('/suppliers', { params });
      setSuppliers(response.data.data);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  // The report's summary, so these tiles match the Reports page exactly.
  const fetchStats = async () => {
    try {
      const response = await api.get('/reports/suppliers', { params: { limit: 1 } });
      setStats(supplierTiles(response.data.summary));
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const getRegistrationChip = (approvalStatus) => {
    const status = ['approved', 'rejected'].includes(approvalStatus) ? approvalStatus : 'pending';
    return <StatusLabel kind="registration" status={status} label={{ approved: 'Approved', rejected: 'Rejected', pending: 'Pending' }[status]} />;
  };

  const formatDate = (date) => {
    if (!date) return '-';

    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const fontBase = {
    fontFamily: 'Roboto, Arial, sans-serif',
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', pb: 0 }}>
      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 3, sm: 5 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
          flex: 1,
        }}
      >
        {/* Page Header */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  ...fontBase,
                  fontWeight: 'bold',
                  mb: 1,
                  color: '#111827',
                  fontSize: { xs: '22px', sm: '28px' },
                  letterSpacing: '-0.01em',
                }}
              >
                {isMobile ? 'Supplier Contract Management' : 'Supplier Management Dashboard'}
              </Typography>
              <Typography
                sx={{
                  ...fontBase,
                  color: '#6b7280',
                  fontSize: { xs: '13px', sm: '14px' },
                  lineHeight: 1.6,
                  fontWeight: 400,
                }}
              >
                {isMobile
                  ? 'Manage your onboarded supplier contracts effortlessly.'
                  : 'Seamless, Smart, and Secure Supplier Onboarding for a Future-Ready Business.'}
              </Typography>
            </Box>
          </Box>

          {/* Summary Cards: one supplier per account; the last three add up to the first */}
          <Grid container spacing={{ xs: 1.75, sm: 2.5 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            {[
              { label: 'Total Suppliers', value: stats.total, note: 'One per supplier account', Icon: Groups2Icon, color: '#111827' },
              { label: 'In Flight', value: stats.inFlight, note: 'Draft, in review or awaiting completion', Icon: HourglassEmptyIcon, color: '#b45309' },
              { label: 'Completed', value: stats.completed, note: 'Onboarding complete', Icon: TaskAltIcon, color: '#578A18' },
              { label: 'Rejected', value: stats.rejected, note: 'Application rejected', Icon: HighlightOffIcon, color: '#b91c1c' },
            ].map(({ label, value, note, Icon, color }) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.25, sm: 2.5 },
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    minHeight: { xs: 128, sm: 136 }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" sx={{ ...fontBase, color: '#6b7280', fontSize: '13px', mb: 1, fontWeight: 400 }}>
                        {label}
                      </Typography>
                      <Typography variant="h5" sx={{ ...fontBase, fontWeight: 600, fontSize: '24px', lineHeight: 1.2, color: '#111827', mb: 0.25 }}>
                        {value}
                      </Typography>
                      <Typography variant="caption" sx={{ ...fontBase, color: '#6b7280', fontSize: '13px', fontWeight: 400 }}>
                        {note}
                      </Typography>
                    </Box>
                    <Icon sx={{ fontSize: 20, color }} />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              sx={{ ...fontBase, fontWeight: 'bold', mb: 0.5, color: '#111827', fontSize: { xs: '22px', sm: '28px' }, letterSpacing: '-0.01em' }}
            >
              Supplier List
            </Typography>
            <Typography sx={{ ...fontBase, color: '#6b7280', fontSize: { xs: '13px', sm: '14px' }, mb: 2, fontWeight: 400 }}>
              View and manage your onboarded suppliers here
            </Typography>
          </Box>

          {/* Search, Filter and Download */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search name, vendor no., reg. no., contact"
              sx={{
                minWidth: { xs: '100%', sm: '180px' },
                maxWidth: { xs: '100%', sm: '320px' },
              }}
            />
            <Tooltip title={sortOrder === 'asc' ? 'Oldest first — click for newest first' : 'Newest first — click for oldest first'}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => { setSortOrder(s => s === 'asc' ? 'desc' : 'asc'); setPage(1); }}
                startIcon={sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                sx={{
                  borderColor: '#d1d5db',
                  color: '#374151',
                  textTransform: 'none',
                  fontSize: '14px',
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                  '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' }
                }}
              >
                {sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
              </Button>
            </Tooltip>
          </Box>
          <Box sx={{ mb: 3 }}>
            <ListFilters filters={filters} onChange={handleFiltersChange} pinnedDepartment={pinnedDepartment} />
          </Box>
        </Box>

        {/* Suppliers Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: '#fff'
            }}
          >
            <Table sx={{ minWidth: { xs: 580, md: 1020 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>Company Name</TableCell>
                  <TableCell sx={{ ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>Status</TableCell>
                  <TableCell sx={{ ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', sm: 'table-cell' } }}>Stage</TableCell>
                  <TableCell sx={{ ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Department</TableCell>
                  <TableCell sx={{ ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Supplier Email</TableCell>
                  <TableCell sx={{ ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Submitted Date</TableCell>
                  <TableCell sx={{ ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Registration</TableCell>
                  <TableCell sx={{ ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Reg. Date</TableCell>
                  <TableCell align="right" sx={{ ...fontBase, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ ...fontBase, color: '#6b7280', fontSize: '13px' }}>
                        No suppliers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  suppliers.map((supplier) => (
                    <TableRow
                      key={supplier._id}
                      hover
                      onClick={() => navigate(`/suppliers/${supplier._id}`)}
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      <TableCell sx={{ ...fontBase, fontSize: '14px', color: '#111827', py: 1.5, fontWeight: 500 }}>
                        {supplier.supplierName || '-'}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <StatusChip status={supplier.status} />
                      </TableCell>
                      <TableCell sx={{ ...fontBase, fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', sm: 'table-cell' } }}>
                        {stageLabelFor(supplier.stage)}
                      </TableCell>
                      <TableCell sx={{ ...fontBase, fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>
                        {supplier.department || (
                          <Typography component="span" sx={{ ...fontBase, color: '#9ca3af', fontSize: '13px' }}>
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...fontBase, fontSize: '14px', color: '#111827', py: 1.5, fontWeight: 400, display: { xs: 'none', md: 'table-cell' } }}>
                        {supplier.companyEmail || supplier.submittedBy?.email || '-'}
                      </TableCell>
                      <TableCell sx={{ ...fontBase, fontSize: '14px', color: '#111827', py: 1.5, fontWeight: 400, display: { xs: 'none', md: 'table-cell' } }}>
                        {formatDate(supplier.submittedAt || supplier.createdAt)}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>
                        {getRegistrationChip(supplier.submittedBy?.supplierApprovalStatus)}
                      </TableCell>
                      <TableCell sx={{ ...fontBase, fontSize: '14px', color: '#111827', py: 1.5, fontWeight: 400, display: { xs: 'none', md: 'table-cell' } }}>
                        {formatDate(supplier.submittedBy?.supplierApprovalReviewedAt)}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <VisibilityIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <DataTablePagination
              page={page}
              total={total}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              loading={loading}
            />
          </TableContainer>
        )}
      </Container>
      <Footer />
    </Box>
  );

};

export default SupplierList;
